import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'light',
  setTheme: () => undefined,
  toggleTheme: () => undefined,
});

const STORAGE_KEY = 'ab_theme';

/**
 * Resolve the initial theme:
 *   1. localStorage value if set ('light' or 'dark')
 *   2. otherwise, OS preference (prefers-color-scheme)
 *   3. otherwise, light
 */
function resolveInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  // Default to light per product requirement; users can flip via toggle.
  return 'light';
}

function applyTheme(t: Theme): void {
  // Single global switch — sets both data-mode (Axis spec) and data-theme
  // (legacy) so every token resolves correctly under [data-mode="dark"].
  const root = document.documentElement;
  if (t === 'dark') {
    root.setAttribute('data-mode', 'dark');
    root.setAttribute('data-theme', 'dark');
  } else {
    root.removeAttribute('data-mode');
    root.removeAttribute('data-theme');
  }
  // Update <meta name="theme-color"> so mobile browser chrome matches (Axis palette)
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', t === 'dark' ? '#1C1A1B' : '#FAF7F8');
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(resolveInitialTheme);

  // Apply on mount and whenever theme changes
  useEffect(() => {
    applyTheme(theme);
    try { window.localStorage.setItem(STORAGE_KEY, theme); } catch { /* private mode */ }
  }, [theme]);

  const value: ThemeContextValue = {
    theme,
    setTheme: setThemeState,
    toggleTheme: () => setThemeState(prev => (prev === 'dark' ? 'light' : 'dark')),
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
